/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */
import constants from '../constants';
import EnvironmentInfo from '../environment-info';
import assetLoader from '../utils/asset-loader';
import Utils from '../utils/utils';
import loadLocalizationAssets from '../initialiser/init-localization';
import { getLocaleUtilsForLanguage } from '../rules/rules-store';

import { localeBundlesManager } from './bundles-manager';
import ParseUtils from './parse-utils';
import type { LocaleJson, LocaleFields, LocaleStore, AppStaticJson, Fields } from './types';

/**
 * @description
 * Provides APIs to create,update and lookup the localization store which is a map of field values.
 */
class LocaleUtils {
  GENERIC_BUNDLE_KEY = '@BASECLASS!GENERIC!PYGENERICFIELDS';
  FIELD_LABELS_BUNDLE_KEY = '@BASECLASS!LABEL!PYFIELDLABELS';
  GENERIC_BUNDLE_KEY_LP = 'GENERIC!GENERICFIELDS';
  MESSAGE_BUNDLE_KEY = '@BASECLASS!MESSAGE!PYMESSAGES';

  BASE_CLASS = '@BASECLASS';

  DATA_PAGE_LOCALECONTEXT = 'datapage';
  localeStore: LocaleStore;
  private timezone?: string;
  private availableLangPacks?: string[];

  constructor() {
    this.localeStore = {};
    this.getLocaleValue = this.getLocaleValue.bind(this);
  }

  /**
   * Sets the localeJson for a Rule, if the localization rule exist it will be deep merged, this is to support the layered bundling
   *
   * @example <caption>PCore.getLocaleUtils().setLocaleForRule({ "fields" : {"First Name" : "Primero nome","Last Name"  : "Ultimo nome"}}, "WORK-HOME!VIEW!PERSONALINFO");</caption>
   * After the API call above the localization Store will have an entry for the "WORK-HOME!VIEW!PERSONALINFO"
   * {
   *      "WORK-HOME!VIEW!PERSONALINFO" : {
   *          "fields" : {
   *                "First Name" : "Primero nome",
   *                "Last Name"  : "Ultimo nome"
   *          }
   *      },
   *      .
   *      .
   *      .
   * }
   * @param localeJson     locale JSON for the rule.
   * @param localeRuleKey  A key in the localization store for which we want to set the localeJSON
   * @function
   */
  setLocaleForRule = (localeJson: AppStaticJson, localeRuleKey: string) => {
    const localeFields = localeJson.LocaleContent;
    const localeRuleKeyCapital = localeRuleKey.toUpperCase();
    if (localeRuleKeyCapital.startsWith('@BASECLASS!DATAPAGE!') && localeRuleKeyCapital.split(/[!#]/).length < 4) {
      Object.entries(localeFields).forEach(([key, value]) => {
        if (value !== null && typeof value === 'object' && 'fields' in value) {
          this.updateLocaleStore(value as LocaleFields, `${localeRuleKeyCapital}#${key}`.toUpperCase());
        }
      });
    } else if (localeRuleKeyCapital === 'GENERIC!GENERICFIELDS') {
      Object.entries(localeFields).forEach(([key, value]) => {
        if (value !== null && typeof value === 'object') {
          this.sanitizeLocaleField(value as LocaleFields, key.toUpperCase());
        }
      });
    } else {
      this.sanitizeLocaleField(localeFields, localeRuleKeyCapital);
    }
  };

  /**
   * @private
   * @description
   * Updates the locale store with the fields from the localeJson, for backward compatibility.
   */
  updateLocaleStore = (localeJson: LocaleFields, localeRuleKey: string) => {
    const fields = localeJson.fields || localeJson;
    const localeRuleKeyFields = this.localeStore[localeRuleKey];
    if (localeRuleKeyFields) {
      Utils.mergeDeep(fields, localeRuleKeyFields);
    } else if (fields && typeof fields === 'object') {
      this.localeStore[localeRuleKey] = fields;
    }
  };

  /**
   *
   * @param localeFields
   * @param localeRuleKey
   * @private
   * @description sanitizes the locale fields of Insight for backward compatibility.
   */
  sanitizeLocaleField = (localeFields: LocaleFields, localeRuleKey: string) => {
    const exploreDataPaths = ['DASHBOARDSMANAGER', 'DATAEXPLORER', 'INSIGHT'];
    if (exploreDataPaths.includes(localeRuleKey.toUpperCase())) {
      Object.entries(localeFields).forEach(([, value]) => {
        if (value !== null && typeof value === 'object' && 'fields' in value) {
          this.updateLocaleStore(value as LocaleFields, localeRuleKey);
        }
      });
    }
    this.updateLocaleStore(localeFields, localeRuleKey);
  };

  /**
   * Gets localeJson for a rule
   *
   * @example <caption>PCore.getLocaleUtils().getLocaleForRule("WORK-HOME!VIEW!PERSONALINFO");</caption>
   * If the localization store looks like below
   * {
   *      "WORK-HOME!VIEW!PERSONALINFO" : {
   *          "fields" : {
   *                "First Name" : "Primero nome",
   *                "Last Name"  : "Ultimo nome"
   *          }
   *      }
   * }
   * The call to the API returns the below JSON
   * {
   *   "fields" : {
   *      "First Name" : "Primero nome",
   *      "Last Name"  : "Ultimo nome"
   *   }
   * }
   * @param localeRuleKey - default value  "@BASECLASS!GENERIC!PYGENERICFIELDS" A key in the localization store for which we want to get the localeJson.
   * @returns The fields object from localization store or undefined if passed key doesn't exists in localization store.
   * @function
   */
  getLocaleForRule(localeRuleKey: string): Fields | undefined {
    return this.localeStore[localeRuleKey];
  }

  /**
   * Resets the localization store to an empty object
   *
   * @example <caption>PCore.getLocaleUtils().resetLocaleStore();</caption>
   * After this call, localization store is reset to an empty object {}.
   * @function
   */
  resetLocaleStore = () => {
    this.localeStore = {};
  };

  /**
   * Returns the localized value of string from the localization store.
   *
   * @example <caption>PCore.getLocaleUtils().getLocaleValue("First Name", "", "WORK-HOME!VIEW!PERSONALINFO");</caption>
   * For a store which looks like the one below
   * {
   *      "WORK-HOME!VIEW!PERSONALINFO" : {
   *          "fields" : {
   *                "First Name" : "Primero nome",
   *                "Last Name"  : "Ultimo nome"
   *          }
   *      }
   * }
   * The API call returns "Primero nome".
   * @param localeKey string to be localized
   * @param localePath Locale category in the localeJson. Default is always fields.
   * @param localeRuleKey Key in the Localization store.
   * @returns the localized value as a string
   * @function
   */
  getLocaleValue(
    localeKey: string,
    localePath?: string,
    localeRuleKey?: string | null,
    componentName?: string | null
  ): string {
    const sanitizedLocaleKey = this.#sanitizeLocaleKey(localeRuleKey, localePath);
    const isDynamicLocale = this.#isDynamicLocale(localeKey);
    if (isDynamicLocale) {
      localeKey = ParseUtils.formatLocaleKey(localeKey);
    }

    const ruleKeyCategory = sanitizedLocaleKey ? this.localeStore[sanitizedLocaleKey.toUpperCase()] ?? {} : {};
    const componentCategory = componentName ? this.localeStore[componentName.toUpperCase()] ?? {} : {};

    if (localeRuleKey?.toUpperCase() === 'MESSAGES' && typeof localeKey === 'string') {
      return this.getLocalizedErrorMessage(ruleKeyCategory, localeKey) || localeKey;
    }
    // TODO: Resolving interpolated or parameter supported localeString
    if (isDynamicLocale) {
      return ParseUtils.replaceWithValue(ruleKeyCategory[localeKey] || componentCategory[localeKey] || localeKey);
    }
    return ruleKeyCategory[localeKey] || componentCategory[localeKey] || localeKey;
  }

  #sanitizeLocaleKey(localeKey?: string | null, localePath?: string): string | undefined {
    if (localeKey?.toUpperCase().startsWith('@BASECLASS!DATAPAGE!')) {
      return localePath ? `${localeKey}#${localePath}` : localeKey;
    }
    return localeKey || localePath;
  }

  /**
   * Returns the localized value of string from the localization store.
   *
   * @param localeRuleKey key in the localized store
   * @param definedOnClass classID of the paragraph rule
   * @param paragraphName name of the paragraph rule
   * @returns the localized value as a string
   * @function
   * @private
   */
  getParagraphLocaleValue(definedOnClass: string, paragraphName: string) {
    const ruleKey = `${definedOnClass}!${constants.PARAGRAPH}!${paragraphName}`;
    const ruleLocaleObj = this.localeStore[ruleKey.toUpperCase()];
    if (ruleLocaleObj) {
      return {
        category: ruleLocaleObj.category,
        content: decodeURIComponent(
          atob(ruleLocaleObj.content)
            .split('')
            .map((c) => {
              return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
            })
            .join('')
        )
      };
    }
    return null;
  }

  /**
   * Returns the locale reference for the portal.
   * @param   {string | null} optional portal name, if not provided default portal name would be used.
   * @returns {string} the localized value as a string
   * @function
   * @example <caption>PCore.getLocaleUtils().getPortalLocaleReference()</caption>
   * this returns a value for example `PORTAL!WEBPORTAL`.
   * warning: only use when pyLocaleReference is not available on portal dx response.
   */
  getPortalLocaleReference(portal: string | null): string {
    const LocaleVersionV2 = 2;
    if (EnvironmentInfo.getLocalizationVersion() !== LocaleVersionV2) {
      return '';
    }

    if (portal) {
      return `PORTAL!${portal}`.toUpperCase();
    }
    return `PORTAL!${EnvironmentInfo.getPortalName()}`.toUpperCase();
  }

  /**
   * Returns the locale reference for the case type.
   * @param   {string} caseClass Class of the case type, ex. APP-REACT-WORK.
   * @returns {string} the localized value as a string
   * @function
   * @example <caption>PCore.getLocaleUtils().getCaseLocaleReference('APP-REACT-WORK')</caption>
   * this returns a value for example `CASE!APP-REACT-WORK`.
   */
  getCaseLocaleReference(caseClass: string): string | null {
    return caseClass ? `CASE!${caseClass}`.toUpperCase() : null;
  }

  /**
   * Returns the locale version.
   *
   * @returns {number} the locale version of the runtime, initialized during bootstrap
   * @function
   * @example <caption>PCore.getLocaleUtils().getLocalizationVersion()</caption>
   * this returns a value for example 2
   * @private
   * @deprecated
   */
  getLocalizationVersion(): number {
    return 2;
  }

  /**
   * Returns an array of localeJson objects for the respective locale references passed to the API.
   * Checks if the locale references are present in client localization store. If not present, makes a call to constellation service and gets the localeJson objects.
   *
   * @example <caption>LocalUtils.getLocaleJsons(["WORK-HOME!PAGE!PERSONALINFO", "WORK-HOME!CASE!FINANCIALSTATUS"])</caption>
   * @param localeRefs Array of locale references rule names.
   * @returns Array of localeJson objects
   * @function
   * @private
   */
  async getLocaleJsons(localeRefs: string[]) {
    if (!(await this.isCurrentLocalePackAvailable())) {
      return [];
    }
    // get locale Jsons from the constellation service
    const appLocale = EnvironmentInfo.getUseLocale();
    const localeJsons: LocaleJson = [];
    const localeRefsInCache = PCore.getLocaleUtils().localeStore;
    localeRefs = localeRefs.filter((localeRef) => {
      return !localeRefsInCache[localeRef];
    });
    localeRefs = localeRefs.map((localeRef) => {
      return localeRef.toUpperCase();
    });
    if (localeRefs.length > 0) {
      const localeRefPromises = localeRefs.map((localeRef) => {
        return {
          localeRuleKey: localeRef,
          promise: assetLoader.getSvcLocale(appLocale, `${localeRef}.json`)
        };
      });
      const results = await Promise.allSettled(localeRefPromises.map((localeRef) => localeRef.promise));

      await Promise.all(
        results.map(async (result, index) => {
          if (result.status === 'fulfilled') {
            const localeRef = localeRefPromises[index];
            let data;
            try {
              data = await result.value.json();
            } catch {
              data = null;
            }
            if (data) {
              localeJsons.push({
                localeRuleKey: localeRef.localeRuleKey,
                jsonData: data
              });
            }
          }
        })
      );
    }
    return localeJsons;
  }

  /**
   * Loads all the locale bundles from the locale code.
   *
   * @example <caption> PCore.getLocaleUtils().loadLocaleResources(["fr-FR", "de-DE"]);   </caption>
   * @param locales list of all the locales to be loaded
   */
  async loadLocaleResources(locales: string[]) {
    await localeBundlesManager.loadLocaleBundle(locales);
    locales.forEach(async (locale) => {
      await loadLocalizationAssets(locale);
    });
  }

  /**
   * This API sets the timezone in the LocaleUtils.
   *
   * @example <caption>PCore.getLocaleUtils().setTimezone("Asia/Calcutta")</caption>
   * //This call sets the time zone to Asia/Calcutta
   * @param timezone TimeZone
   */
  setTimezone(timezone: string) {
    this.timezone = timezone;
  }

  /**
   * This API returns the timezone in use if it is set, else returns the system/browser timezone.
   * @example <caption>PCore.getLocaleUtils().getTimeZoneInUse()</caption>
   * which should return "Asia/Calcutta" if "Asia/Calcutta" is set at requestor page.
   *
   * @returns the timezone
   * @function
   */
  getTimeZoneInUse(): string {
    const SystemTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const TzInUse = this.timezone;
    return TzInUse || SystemTz;
  }

  /**
   * Finding the placeholders' value in the error message keys like {1}, {2}..
   * @param {Array} patternParts
   * @param {String} text - Error message key
   * @returns {Array} of placeholders which are to be replaced in translation
   * @private
   */
  matchPatternWithText(patternParts: string[], text: string) {
    let remainingText = text;
    const placeholders = [];

    for (let i = 0; i < patternParts.length; ) {
      const part = patternParts[i];
      if (part.length !== 0) {
        const index = remainingText.indexOf(part);
        // If the part is not found, it means the pattern doesn't match
        if (index === -1) {
          return null;
        }
        if (i > 0) {
          const placeholderValue = remainingText.slice(0, index);
          placeholders.push(placeholderValue);
        }
        remainingText = remainingText.slice(index + part.length);
      }
      i += 1;
    }
    if (remainingText) {
      placeholders.push(remainingText);
    }
    return placeholders;
  }

  /**
   * Returns localized error message for given input
   * @param {object} messages
   * @param {String} text - text to be found in message translations
   * @returns {String}
   * @private
   */
  getLocalizedErrorMessage(messages: { [key: string]: string }, text: string) {
    let translation = '';
    // First checking for normal usecase when no params are present.
    // Error like : `Invalid E-mail OR Resource not found will be directly translated`
    if (messages[text]) {
      return messages[text];
    }
    // Iterating over all the messages to find out pattern and replace the params in translations
    for (const pattern of Object.keys(messages)) {
      // finding out the {1}, {2},... {n} placeholders
      const patternParts = pattern.split(/\{\d+\}/);
      const placeholders = this.matchPatternWithText(patternParts, text);

      if (placeholders) {
        const tempTranslation = messages[pattern];
        translation = this.replacePlaceHolders(placeholders, tempTranslation);
        if (translation) {
          return translation;
        }
      }
    }
    return translation;
  }

  /**
   * Replace placeholders with their value if all placeholders are found in localized string
   * @param {Array<String>} placeholderArr
   * @param {String} translation
   * @returns {String} localized error message after replacing the placeholders
   * @private
   */
  replacePlaceHolders(placeholderArr: string[], translation: string) {
    for (let i = 0; i < placeholderArr.length; ) {
      if (!translation.includes(`{${i + 1}}`)) {
        return '';
      }
      translation = translation.replace(`{${i + 1}}`, placeholderArr[i]);
      i += 1;
    }
    return translation;
  }

  /**
   * This API returns if the current user's locale present in the available locales in Constellation service
   * @example <caption>PCore.getLocaleUtils().isCurrentLocalePackAvailable()</caption>
   * which should return true/false based on if the current user's locale is available in Constellation service.
   * @returns true/false
   * @function
   * @private
   */
  async isCurrentLocalePackAvailable() {
    const userLocale = EnvironmentInfo.getLocale();
    if (!this.availableLangPacks) {
      this.availableLangPacks = await assetLoader.getSvcAvailableLocales();
    }
    return userLocale && this.availableLangPacks.includes(userLocale);
  }

  /**
   * This API returns translatations of all labels under `CosmosFields`
   * OR undefined if there is no translations present in locale store
   * @example <caption>Example for getBaseComponentLabels</caption>
   * PCore.getLocaleUtils().getLocalizationFields()
   * @returns {Fields | undefined} return translations under CosmosFields
   * @private
   */
  getBaseComponentLabels = (): Fields | undefined => {
    return this.getLocaleForRule('COSMOSFIELDS');
  };

  #isDynamicLocale(localeKey: string): boolean {
    let isDynamicLocale = false;
    if (localeKey && typeof localeKey === 'string') {
      isDynamicLocale = localeKey.includes('@P ');
    }
    return isDynamicLocale;
  }
}

const getLocaleUtils = (locale?: string): LocaleUtils => {
  const languagePackObj = locale ?? EnvironmentInfo.getLocale() ?? 'en-US';
  return getLocaleUtilsForLanguage(languagePackObj);
};

export { getLocaleUtils, LocaleUtils };
